import React, { useEffect } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { FormField } from "components/form/FormField/FormField";
import { RichEditor } from "components/RichEditor/RichEditor";
import { TemplateSectionPicker } from "components/TemplateSectionPicker/TemplateSectionPicker";
import { TemplateVariablePicker } from "components/TemplateVariablePicker/TemplateVariablePicker";
import { Button } from "components/ui/Button";
import { Dialog, DialogFooter } from "components/ui/Dialog";
import { onInvalidSubmit } from "utils/reportFormErrors";

import styles from "../../../shared/templateModal.module.css";

const isEditorEmpty = (value) =>
  !value || value.replace(/<[^>]*>/g, "").trim().length === 0;

const letterTemplateSchema = z.object({
  name: z.string().min(1, "Please enter template name."),
  template: z
    .string()
    .refine((val) => !isEditorEmpty(val), "Please enter template content."),
});

const DEFAULT_VALUES = {
  name: "",
  template: "",
};

export const LetterTemplateModal = ({
  isOpen,
  onClose,
  onSubmit,
  editTemplate,
}) => {
  const methods = useForm({
    resolver: zodResolver(letterTemplateSchema),
    defaultValues: DEFAULT_VALUES,
  });

  const { control, handleSubmit, reset, getValues, setValue } = methods;

  const handleInsertVariable = (placeholder) => {
    const current = getValues("template") || "";
    setValue("template", current + placeholder, { shouldDirty: true });
  };

  const handleInsertSection = (sectionHtml) => {
    const current = getValues("template") || "";
    setValue("template", current + sectionHtml, { shouldDirty: true });
  };

  useEffect(() => {
    if (!isOpen) return;

    reset(
      editTemplate
        ? {
            name: editTemplate.name ?? "",
            template: editTemplate.template ?? "",
          }
        : DEFAULT_VALUES
    );
  }, [isOpen, editTemplate, reset]);

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      title={editTemplate ? "Edit Letter Template" : "Add New Letter Template"}
      width={900}
    >
      <FormProvider {...methods}>
        <form className={styles.form} onSubmit={handleSubmit(onSubmit, onInvalidSubmit("LetterTemplateModal"))}>
          <div className={styles.fields}>
            <div className={styles.fieldGroup}>
              <label className={styles.label}>Template Name</label>
              <FormField name="name">
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <input
                      {...field}
                      type="text"
                      placeholder="Template Name"
                      className={styles.input}
                    />
                  )}
                />
              </FormField>
            </div>

            <div className={styles.fieldGroup}>
              <div className={styles.labelRow}>
                <label className={styles.label}>Template Content</label>
                <div className={styles.labelActions}>
                  <TemplateSectionPicker onInsert={handleInsertSection} />
                  <TemplateVariablePicker onInsert={handleInsertVariable} />
                </div>
              </div>

              <FormField name="template">
                <Controller
                  name="template"
                  control={control}
                  render={({ field }) => (
                    <RichEditor
                      editorValue={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
              </FormField>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              size="medium"
              type="button"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button variant="primary" size="medium" type="submit">
              Save
            </Button>
          </DialogFooter>
        </form>
      </FormProvider>
    </Dialog>
  );
};
